"use client";

import React, { useEffect, useRef } from "react";
import Image from "next/image";
import Link from "next/link";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { FaChalkboardTeacher, FaLaptopCode, FaUserGraduate, FaBriefcase, FaArrowRight } from "react-icons/fa";

const AboutUsSection = () => {
  const sectionRef = useRef(null);
  const imageWrapperRef = useRef(null);
  const contentRef = useRef(null);
  const statsRef = useRef(null);

  useEffect(() => {
    // Check if window is defined (client-side)
    if (typeof window !== "undefined") {
      gsap.registerPlugin(ScrollTrigger);
      
      const ctx = gsap.context(() => {
        // 1. Image Group Slide In
        gsap.fromTo(imageWrapperRef.current, 
          { x: -50, opacity: 0 },
          {
            x: 0,
            opacity: 1,
            duration: 1,
            ease: "power3.out",
            scrollTrigger: {
              trigger: imageWrapperRef.current,
              start: "top 80%",
            }
          }
        );

        // 2. Content Stagger Fade Up
        const contentChildren = contentRef.current?.children;
        if (contentChildren) {
          gsap.fromTo(contentChildren,
            { y: 30, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.8,
              stagger: 0.1,
              ease: "power3.out",
              scrollTrigger: {
                trigger: contentRef.current,
                start: "top 75%",
              }
            }
          );
        }

        // 3. Number Counter Animation
        const statNumber = statsRef.current?.querySelector(".stat-digit");
        if (statNumber) {
          gsap.fromTo(statNumber,
            { innerText: 0 },
            {
              innerText: 5,
              duration: 2,
              snap: { innerText: 1 },
              ease: "power1.inOut",
              scrollTrigger: {
                trigger: statsRef.current,
                start: "top 85%",
              },
              onUpdate: function() {
                this.targets()[0].innerText = Math.ceil(this.targets()[0].innerText);
              }
            }
          );
        }

      }, sectionRef);

      return () => ctx.revert();
    }
  }, []);

  const features = [
    { icon: <FaChalkboardTeacher />, title: "Expert Mentors", desc: "Learn from industry veterans." },
    { icon: <FaLaptopCode />, title: "Live Projects", desc: "Build real-world applications." },
    { icon: <FaUserGraduate />, title: "Flexible Learning", desc: "Study at your own pace." },
    { icon: <FaBriefcase />, title: "Career Support", desc: "100% Placement assistance." },
  ];

  return (
    <section ref={sectionRef} className="relative py-24 bg-slate-950 overflow-hidden font-sans">
      
      {/* --- Background Effects --- */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-600/10 rounded-full blur-[120px] pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-purple-600/10 rounded-full blur-[100px] pointer-events-none"></div>

      <div className="container mx-auto px-6 max-w-7xl relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          
          {/* --- Left Column: Images --- */}
          <div ref={imageWrapperRef} className="relative">
            {/* Main Image */}
            <div className="relative z-10 rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
              <Image
                src="/images/about-left.jpg" // Ensure this image exists in public/images
                width={600}
                height={500}
                alt="Students Collaboration"
                className="object-cover w-full h-auto hover:scale-105 transition-transform duration-700"
              />
            </div>

            {/* Secondary Image (Floating) */}
            <div className="absolute -bottom-10 -right-10 z-20 w-3/5 rounded-2xl overflow-hidden border-4 border-slate-950 shadow-2xl hidden md:block">
              <Image
                src="/images/about-right.jpg" // Ensure this image exists
                width={300}
                height={250}
                alt="Online Learning"
                className="object-cover w-full h-auto"
              />
            </div>

            {/* Floating Glass Stats Badge */}
            <div ref={statsRef} className="absolute -top-6 -left-6 z-30 bg-white/10 backdrop-blur-md border border-white/20 p-6 rounded-2xl shadow-xl flex flex-col items-center justify-center text-white min-w-[120px]">
              <span className="text-4xl font-bold text-black flex">
                <span className="stat-digit">0</span>+
              </span>
              <span className="text-xs font-medium uppercase tracking-wider text-black mt-1">Years Exp.</span>
            </div>
            
            {/* Dot Pattern Decoration */}
            <div className="absolute -top-10 -right-10 z-0 opacity-30">
               <svg width="100" height="100" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <pattern id="dotPattern" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                     <circle cx="2" cy="2" r="2" className="text-blue-500" fill="currentColor" />
                  </pattern>
                  <rect width="100" height="100" fill="url(#dotPattern)" />
               </svg>
            </div>
          </div>

          {/* --- Right Column: Content --- */}
          <div ref={contentRef} className="space-y-8">
            
            {/* Header */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-bold uppercase tracking-wide mb-4">
                About Vidyastu
              </div>
              <h2 className="text-3xl md:text-5xl font-bold text-white leading-tight">
                Empowering Minds, <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500">
                  Transforming Careers.
                </span>
              </h2>
            </div>

            {/* Description */}
            <p className="text-slate-400 text-lg leading-relaxed">
              We bridge the gap between traditional education and industry demands. 
              Our mission is to provide accessible, high-quality technical training 
              that empowers you to master coding, data science, and design from anywhere in the world.
            </p>

            {/* Features Grid */}
            <div className="grid sm:grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <div key={index} className="group p-4 rounded-xl bg-slate-900 border border-slate-800 hover:border-blue-500/30 transition-all duration-300 hover:bg-slate-800/50">
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-blue-600/10 text-blue-400 group-hover:text-white group-hover:bg-blue-600 transition-colors">
                      {feature.icon}
                    </div>
                    <div>
                      <h4 className="text-white font-semibold text-sm">{feature.title}</h4>
                      <p className="text-slate-500 text-xs mt-1">{feature.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <div className="pt-4">
              <Link href="/about">
                <button className="group flex items-center gap-2 px-8 py-4 bg-white text-slate-950 font-bold rounded-full transition-all hover:bg-blue-50 shadow-lg hover:shadow-white/10 hover:-translate-y-1">
                  Read Our Story
                  <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                </button>
              </Link>
            </div>

          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutUsSection;